import { Component, ElementRef, EventEmitter, Input, OnDestroy, Output, Renderer2 } from '@angular/core';
import { BehaviorSubject, combineLatest, from, Observable, Subject } from 'rxjs';
import {
  delay,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  map,
  pairwise,
  shareReplay,
  skip,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { PaymentPlatformService } from '../../../services';
import { StripePlatformService } from '../../services';
import { STRIPE_DEFAULT_ADDRESS_ELEMENT_OPTIONS, StripeAddressElementCustomOptions, StripElementsInitializationData } from '../../models';
import { BillingDetails } from '@aiwyn/api';
import { PaymentMethod, StripeAddressElement, StripeAddressElementOptions } from '@stripe/stripe-js';
import { applicationDebounceTime, CountryAlpha2Code, ignoreFalsy, ignoreNullish, Logger } from '@aiwyn/shared';

const STRIPE_HEIGHT_TRANSITION_DELAY_MS = 20;
const STRIPE_HEIGHT_TRANSITION_DURATION_MS = 350;

const DISALLOWED_COUNTRIES = [
  CountryAlpha2Code.AMERICAN_SAMOA,
  CountryAlpha2Code.CUBA,
  CountryAlpha2Code.IRAN,
  CountryAlpha2Code.NORTH_KOREA,
  CountryAlpha2Code.MARSHALL_ISLANDS,
  CountryAlpha2Code.MICRONESIA,
  CountryAlpha2Code.PALAU,
  CountryAlpha2Code.SYRIA,
  CountryAlpha2Code.UNKNOWN,
];
const ALLOWED_COUNTRIES = Object.values(CountryAlpha2Code).filter(code => !DISALLOWED_COUNTRIES.includes(code));

@Component({
  selector: 'app-stripe-address-element',
  templateUrl: './stripe-address-element.component.html',
  styleUrls: ['./stripe-address-element.component.scss'],
  standalone: false,
})
export class StripeAddressElementComponent implements OnDestroy {
  @Input() billingDetails?: BillingDetails;
  @Input() forceVisible = false;
  @Output() visible = new EventEmitter<boolean>();

  private readonly _unsubscribe$ = new Subject<void>();

  isAddressRequired$ = this.paymentPlatformService.isAddressRequired().pipe(
    ignoreNullish(),
    distinctUntilChanged(),
    shareReplay(1),
  );

  /**
   * Emits only when the address element, specifically, was deleted. Does not emit when both elements are deleted.
   */
  addressElementDeleted$ = this.stripePlatformService.paymentElement$.pipe(
    ignoreNullish(),
    switchMap(_ => this.stripePlatformService.addressElement$),
    pairwise(),
    map(([previous, current]) => previous != undefined && current == undefined),
    ignoreFalsy(),
  );

  initializeStripeAddressElement$ = combineLatest([
    this.stripePlatformService.getStripeElementInitializationData('address').pipe(
      // new setup intent client secret signals stripe elements are to be created (or refreshed in the event of an invalid payment method)
      distinctUntilKeyChanged('setupIntentClientSecret'),
    ),
    /**
     * If the address element has been deleted, wait until it is required before initializing it again.
     * Immediate re-initialization causes some of Stripe's validation to disappear (fun, I know).
     */
    this.addressElementDeleted$.pipe(
      switchMap(_ => this.isAddressRequired$.pipe(
        ignoreFalsy(),
        take(1),
      )),
      startWith(false), // don't block first initialization
    ),
  ]).pipe(
    tap(([initializationData]) => this._initializeStripeAddressElement({
      ...initializationData,
      newBillingDetails: this.billingDetails ?? initializationData.newBillingDetails,
    })),
    takeUntil(this._unsubscribe$),
  );

  //#region transition logic
  private readonly _isTransitionActive$ = new BehaviorSubject<boolean>(false);
  private readonly _emissionsDuringTransition$ = new BehaviorSubject<number>(0);
  private readonly _shouldCancelPreviousEmission$ = this._isTransitionActive$.pipe(
    ignoreFalsy(),
    tap(_ => this._emissionsDuringTransition$.next(this._emissionsDuringTransition$.value + 1)),
    delay(STRIPE_HEIGHT_TRANSITION_DELAY_MS + STRIPE_HEIGHT_TRANSITION_DURATION_MS),
    tap(_ => this._isTransitionActive$.next(false)),
    takeUntil(this._unsubscribe$),
  );

  manageAddressElementVisibility$ = this.isAddressRequired$.pipe(
    tap(_ => this._isTransitionActive$.next(true)),
    tap(isAddressRequired => {
      if (isAddressRequired) {
        this.visible.emit(true);
        this.renderer.removeStyle(this.host.nativeElement, 'display');
        this.renderer.removeAttribute(this.host.nativeElement, 'aria-hidden');
      }
    }),
    delay(STRIPE_HEIGHT_TRANSITION_DELAY_MS),
    tap(isAddressRequired => {
      if (!isAddressRequired) {
        this.renderer.addClass(this.host.nativeElement, 'stripe-address-element-hidden');
      } else {
        this.renderer.removeClass(this.host.nativeElement, 'stripe-address-element-hidden');
      }
    }),
    delay(STRIPE_HEIGHT_TRANSITION_DURATION_MS),
    filter(_ => {
      if (this._emissionsDuringTransition$.value > 0) {
        this._emissionsDuringTransition$.next(this._emissionsDuringTransition$.value - 1);
        return false;
      }

      return true;
    }),
    tap(isAddressRequired => {
      if (!isAddressRequired) {
        this.visible.emit(false);
        this.renderer.setStyle(this.host.nativeElement, 'display', 'none');
        this.renderer.setAttribute(this.host.nativeElement, 'aria-hidden', 'true');
      }
    }),
    takeUntil(this._unsubscribe$),
  );
  //#endregion

  onBillingDetailsChange$ = this.stripePlatformService.onAddressElementChange$.pipe(
    applicationDebounceTime(),
    ignoreNullish(),
    skip(1),
    filter(_ => !this.host.nativeElement.classList.contains('stripe-address-element-hidden')),
    map(({ value }) => value),
    map(billingDetails => this.paymentPlatformService.marshalBillingDetails(billingDetails as PaymentMethod.BillingDetails)),
    switchMap(marshaledBillingDetails => this.paymentPlatformService.setNewBillingDetails(marshaledBillingDetails)),
    takeUntil(this._unsubscribe$),
  );

  constructor(
    private readonly host: ElementRef<StripeAddressElementComponent & HTMLElement>,
    private readonly paymentPlatformService: PaymentPlatformService,
    private readonly stripePlatformService: StripePlatformService,
    private readonly renderer: Renderer2,
    private readonly logger: Logger,
  ) {}

  ngAfterViewInit(): void {
    this.initializeStripeAddressElement$.subscribe();
    this.onBillingDetailsChange$.subscribe();

    if (!this.forceVisible) {
      this._shouldCancelPreviousEmission$.subscribe();
      this.manageAddressElementVisibility$.subscribe();
    }
  }

  private _initializeStripeAddressElement({ newBillingDetails }: StripElementsInitializationData): void {
    const addressElementCustomOptions = this._getAddressElementCustomOptions(newBillingDetails);
    const addressElementOptions = this._getAddressElementOptions(addressElementCustomOptions);

    const stripeElements = this.stripePlatformService.elements$.value;
    if (stripeElements.getElement('address') != undefined) {
      // Same race as the payment element: creating while a previous address element is still registered throws
      // IntegrationError and kills this subscription (PP-3574). Destroy the previous element first.
      this.logger.warn(
        'PAYMENT : StripeAddressElementComponent : _initializeStripeAddressElement : Destroying previously registered address element.',
      );
      this.stripePlatformService.destroyAddressElement();
    }

    const addressElement = stripeElements.create('address', addressElementOptions);

    this.stripePlatformService.addressElement$.next(addressElement);
    this.stripePlatformService.addressElement$.value.on('change', event => this.stripePlatformService.onAddressElementChange$.next(event));
    this.stripePlatformService.addressElement$.value.mount(this.host.nativeElement);
  }

  private _getAddressElementCustomOptions(billingDetails: BillingDetails): StripeAddressElementCustomOptions {
    const defaultValues: StripeAddressElementCustomOptions = billingDetails != undefined
      ? { defaultValues: this.paymentPlatformService.unmarshalBillingDetails(billingDetails) }
      : {};

    return {
      ...defaultValues,
      allowedCountries: ALLOWED_COUNTRIES,
    };
  }

  private _getAddressElementOptions(customOptions: StripeAddressElementCustomOptions = {}): StripeAddressElementOptions {
    return {
      ...STRIPE_DEFAULT_ADDRESS_ELEMENT_OPTIONS,
      allowedCountries: customOptions.allowedCountries ?? undefined,
      contacts: customOptions.contacts ?? undefined,
      defaultValues: customOptions.defaultValues ?? undefined,
    };
  }

  /**
   * Currently only used for updating the billing address of an existing saved payment method that has incomplete billing details.
   */
  validate(): Observable<boolean> {
    return from(this.stripePlatformService.elements$.value.submit()).pipe(
      take(1),
      map(({ error }) => error == undefined),
    );
  }

  /**
   * Clearing the address element (and probably any stripe element) only seems to work when the element is mounted (AND there seems to be a
   * delay, so you can't just synchronously call clear then destroy/unmount).
   */
  onClose(): Observable<StripeAddressElement> {
    return this.stripePlatformService.addressElement$.pipe(
      take(1),
      tap(addressElement => addressElement?.clear()),
    );
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();

    this.stripePlatformService.destroyAddressElement();
  }
}
